import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useImmerReducer } from 'use-immer';
import { useFeatures } from '@nextiva/feature-toggle';
import { dataSelector } from '@nextiva/global-store';

import AsyncAccessView from './AsyncAccessView';
import { reducer } from './reducer';
import { FeatureContext, InitialState, actionTypes } from './featureContext';
import { featureFlagService } from './featureFlagService';

const flagNames = [
  {
    name: 'PlatformAdmin.LocationBasedAdmin',
    type: actionTypes.LOCATION_BASED_ADMIN_FLAG,
  },
  { name: 'PlatformAdmin.UseAuthV3', type: actionTypes.AUTHV3_FLAG },
  { name: 'PlatformAdmin.VoiceActions', type: actionTypes.VOICE_ACTIONS_FLAG },
  { name: 'Global.Pendo', type: actionTypes.PENDO_FLAG },
  {
    name: 'PlatformAdmin.BulkImports.Rearchitecture',
    type: actionTypes.BULK_IMPORTS_REARCHITECTURE,
  },
  {
    name: 'PlatformAdmin.BulkImports.Teams',
    type: actionTypes.BULK_IMPORTS_TEAMS,
  },
  {
    name: 'PlatformAdmin.BulkImports.Locations',
    type: actionTypes.BULK_IMPORTS_LOCATIONS,
  },
  { name: 'Global.Navigation', type: actionTypes.GLOBAL_NAVIGATION_FLAG },
  { name: 'PlatformAdmin.NewVoicemail', type: actionTypes.NEW_VOICEMAIL_FLAG },
  { name: 'PlatformAdmin.TaxId', type: actionTypes.ACCOUNT_INFO_TAX_ID_FLAG },
  {
    name: 'PlatformAdmin.TeamsSMS.Licensing',
    type: actionTypes.TEAMS_SMS_LICENSING,
  },
  {
    name: 'PlatformAdmin.TaxIdLink',
    type: actionTypes.ACCOUNT_INFO_TAX_ID_LINK_FLAG,
  },
  {
    name: 'PlatformAdmin.SMSCampaignRegistry.GenerateContent',
    type: actionTypes.SMS_CAMPAIGN_REGISTRY_GENERATE_CONTENT,
  },
  {
    name: 'PlatformAdmin.TeamsSMS',
    type: actionTypes.TEAMS_SMS,
  },
  {
    name: 'PlatformAdmin.PurchaseLicenseFlow',
    type: actionTypes.PURCHASE_LICENSE_FLOW,
  },
  {
    name: 'PlatformAdmin.UserImport.WelcomeEmail',
    type: actionTypes.WELCOME_EMAIL,
  },
  {
    name: 'Launchpad.Connect',
    type: actionTypes.CONNECT_FLAG,
  },
  {
    name: 'Global.NextivaCX.EnableFlow',
    type: actionTypes.GLOBAL_NEXTIVACX_FLOW,
  },
  {
    name: 'PlatformAdmin.BulkUsersEdit',
    type: actionTypes.BULK_USERS_EDIT,
  },
  {
    name: 'PlatformAdmin.AllowCxPrimaryOwnerDeletion',
    type: actionTypes.ALLOW_CX_PRIMARY_OWNER_REMOVAL,
  },
  {
    name: 'PlatformAdmin.SMSCampaignRegistry.MessagingPageUpdates.SmsUseCases',
    type: actionTypes.SMS_USE_CASES,
  },
  {
    name: 'PlatformAdmin.SMSCampaignRegistry.Autom8tly',
    type: actionTypes.AUTOM8TLY,
  },
];

function checkIfFeaturesAreLoaded(featuresStatus) {
  return featuresStatus.every(({ isReady }) => isReady);
}

const useFlagCheck = (dispatch) => {
  const { isFeatureEnable, isFeatureReady } = useFeatures();

  const featuresStatus = flagNames.map(({ name, type }) => ({
    type,
    isReady: isFeatureReady(name),
    isEnable: isFeatureEnable(name),
  }));

  const areFlagsLoaded = checkIfFeaturesAreLoaded(featuresStatus);

  useEffect(() => {
    if (areFlagsLoaded) {
      featuresStatus.forEach(({ type, isEnable }) => {
        dispatch({ type, state: isEnable });
      });
    }
  }, [areFlagsLoaded]);
};

export const FeatureFlag = (props) => {
  const [featureState, dispatch] = useImmerReducer(reducer, InitialState);
  const globalStoreData = useSelector(dataSelector);

  const checkClusterForBWPatching = (clusterId) => {
    featureFlagService
      .HasItemInStrategy(
        'broadworks.patching.support',
        'clusterListForBWPatching',
        'clusterIds',
        clusterId,
        {
          ssoToken: props.ssoToken,
          isZeroTrust: props.isZeroTrust,
        },
      )
      .then((response) => {
        dispatch({
          type: actionTypes.IS_CLUSTERID_IN_BW_PATCHING,
          state: response.data,
        });
      });
  };

  useEffect(() => {
    if (globalStoreData?.user?.accountInfo?.clusterId) {
      checkClusterForBWPatching(globalStoreData?.user?.accountInfo?.clusterId);
    }
  }, [globalStoreData?.user?.accountInfo?.clusterId]);

  useFlagCheck(dispatch);

  useEffect(() => {
    if (!featureState) return;
    const flags = Object.values(featureState).some((v) => v === null);

    if (!flags) {
      dispatch({
        type: actionTypes.UPDATE_IS_LOADING,
        state: false,
      });
    }
  }, [featureState]);

  const contextValue = useMemo(
    () => ({
      featureState,
      dispatch,
      corpAcctNbr: globalStoreData.user?.profileDTO.corpAcctNbr,
    }),
    [featureState, dispatch, globalStoreData.user],
  );

  return (
    <FeatureContext.Provider value={contextValue}>
      <AsyncAccessView
        isLoading={globalStoreData.isLoading || featureState.isLoading}
        loadingMessage="Loading"
        error={
          globalStoreData.isError &&
          'Unable to retrieve your available licenses. Please reload the page.'
        }
        hasAccess
      >
        {props.children}
      </AsyncAccessView>
    </FeatureContext.Provider>
  );
};
